// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SearchInputStyles } from "@ces/Overrides/Widgets/Header/SearchInput";
import { constants } from "@ces/XDL/constants";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { color, styledScrollbar, xScreen } from "@ces/XDL/Utilities";
import { searchInputStyles } from "@insite/content-library/Widgets/Header/HeaderSearchInput";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { css } from "styled-components";

const sharedTextStyle: TypographyPresentationProps = {
    ellipsis: false,
    size: 12,
    lineHeight: "13px",
    weight: fontConfig.weight.regular,
    css: css`
        margin: 0;
    `,
};

const sharedHeadStyle: TypographyPresentationProps = {
    variant: "h6",
    css: css`
        margin: 0;
        margin-bottom: -3px; // to revert gap.
    `,
};

searchInputStyles.searchInputStyles = {
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ width }: any) => `width: ${width}px;`}
            ${xScreen("lte", "md")} {
                flex-direction: column;
                width: 100%;
                height: calc(100vh - ${constants.header.tablet.size.height}px);
                overflow: auto;
            }
            ${xScreen("lte", "sm")} {
                overflow: auto;
                ${styledScrollbar({ margined: 14 })};
                height: calc(100vh - ${constants.header.mobile.size.height}px - 2px);
                padding-bottom: 4px;
                margin-bottom: 4px;
            }
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            padding: 17px 8px 17px 17px;
            width: auto;
            flex: 1;
            gap: 24px;
            min-width: 33%;
            ${({ hasContent }: any) =>
                hasContent
                    ? css`
                          border-right: 1px solid ${color("common.border")};
                      `
                    : ""};
            max-height: calc(95vh - ${constants.header.size.height}px);
            ${styledScrollbar({ margined: 14 })};
            ${xScreen("lte", 1260)} {
                width: 400px;
            }
            ${xScreen("lte", "md")} {
                max-height: 100%;
                height: 100%;
                width: 100%;
                min-width: unset;
                overflow: unset;
            }
            ${xScreen("lte", "md")} {
                ${({ productsWrapper }: any) =>
                    productsWrapper ? "padding: 16px 0;" : "padding: 16px 0 24px 0; flex:0;"};
                margin: 0 8px 0 17px;
                width: auto;
                ${({ hasContent }: any) =>
                    hasContent
                        ? css`
                              border-right: 0;
                              border-bottom: 1px solid ${color("common.border")};
                          `
                        : ""};
            }
        `,
    },
    blockWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 15px;
            &:empty {
                display: none;
            }
        `,
    },
    autocompleteCategoriesStyles: {
        headerText: sharedHeadStyle,
        categoryItemText: sharedTextStyle,
        focusedCategoryItemText: sharedTextStyle,
        link: {
            typographyProps: sharedTextStyle,
        },
        categorySubtitle: {
            css: css`
                &:hover,
                &:focus {
                    color: ${color("common.ui8")};
                }
            `,
        },
    },
    autocompleteBrandsStyles: {
        headerText: sharedHeadStyle,
        brandItemText: sharedTextStyle,
        link: {
            typographyProps: sharedTextStyle,
        },
        focusedLink: {
            typographyProps: sharedTextStyle,
        },
        focusedBrandItemText: sharedTextStyle,
    },
    autocompleteContentStyles: {
        headerText: sharedHeadStyle,
        focusedLink: {
            typographyProps: sharedTextStyle,
        },
        link: {
            typographyProps: sharedTextStyle,
        },
    },
    mobileInputWrapper: {
        css: css`
            ${xScreen("lte", 767)} {
                position: fixed;
                top: 0;
                left: 0;
                display: flex;
                width: 100%;
                background: white;
                padding: 9px 16px;
                gap: 16px;
            }
        `,
    },
    searchPopUpWrapper: {
        css: css`
            ${({ width }: any) => `width: ${width}px;`}
        `,
    },
    searchHistoryStyles: {
        wrapper: {
            css: css`
                width: 100%;
            `,
        },
    },
    tabletInputWrapper: {
        css: css`
            width: 100%;
            display: flex;
            gap: 16px;
        `,
    },
    autocompleteProductsStyles: {
        wrapper: {
            css: css`
                ${xScreen("lte", "md")} {
                    margin-top: 8px;
                }
            `,
        },
    },
    input: {
        cssOverrides: {
            formInputWrapper: css`
                ${xScreen("lte", "md")} {
                    max-width: 407px;
                }
                ${xScreen("lte", "sm")} {
                    max-width: unset;
                }
            `,
        },
    },
} as SearchInputStyles;
